import { createFileRoute, Link, useLocation } from '@tanstack/react-router'
import { noop, useSuspenseQuery } from '@tanstack/react-query'
import { useEffect, type ReactNode } from 'react'
import { trpc } from '@/main'
import { authViewTransition, cn } from '@/lib/utils'
import { faqPageJsonLd, organizationJsonLd, pageSeo } from '@/lib/seo'
import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { Appear } from '@/components/custom/appear'
import { Button, ButtonLink } from '@/components/ui/button'
import { Footer } from './-landing/footer'
import { LandingCounts, LandingCountsValue } from './-landing/landing-counts'
import { LandingNav } from './-landing/nav'
import { ProfessionalsSection } from './-landing/professionals-section'
import { RadarCompare } from './-landing/radar-compare'
import { ApiSection } from './-landing/api-section'
import { PlansSection } from './-landing/plans-section'
import { contactFieldReady } from './-landing/contact-form-on-appear'
import { FaqSection } from './-landing/faq-section'
import { LANDING_FAQ } from './-landing/faq-content'
import { InkBackdrop, INK } from './-landing/ink-backdrop'
import { Reveal } from './-landing/reveal'
import { DemoChevron, LinkChevron } from './-landing/demo-chevron'
import { FEATURE_FLAGS } from './-landing/feature-flags'
import { SolutionAnimation, type SolutionKind } from './-landing/solution-animations'
import { usePauseOffscreen } from './-landing/use-pause-offscreen'

export const Route = createFileRoute('/')({
  context: () => ({ landingCountsQueryOptions: trpc.common.getLandingCounts.queryOptions() }),
  loader: ({ context }) => {
    context.queryClient.query(context.landingCountsQueryOptions).catch(noop)
  },
  head: (ctx) =>
    pageSeo(ctx, {
      path: '/',
      title: msg`Psychographic talent assessment software`,
      description: msg`Get to know the psychographic profile of your employees, positions and teams in depth, and decide on talent with the confidence that data gives you.`,
      jsonLd: ({ description, translate }) => [
        ...organizationJsonLd(description),
        faqPageJsonLd(translate, LANDING_FAQ),
      ],
    }),
  component: RouteComponent,
  pendingComponent: () => <div className="min-h-dvh" />,
})

/**
 * Every "Get in touch" leads to the contact form at the page's close: scroll there, cursor in its first field.
 * The form mounts when its section comes near, so the field is awaited rather than read on this frame — see
 * `contactFieldReady`, which also owns the honeypot exclusion.
 */
function requestDemo() {
  const contact = document.getElementById('contact')
  const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  contact?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' })
  if (contact) void contactFieldReady(contact).then((field) => field?.focus({ preventScroll: true }))
}

/**
 * The same arrival, but by URL: the top bar on the policy pages links to `/#contact` instead of calling
 * `requestDemo` (it is not on this page to call it), and a pasted or shared link comes in the same way. The
 * router has only scrolled by then, so this finishes the journey wherever it started.
 */
function useContactHash() {
  const hash = useLocation({ select: (location) => location.hash })
  useEffect(() => {
    if (hash === 'contact') requestDemo()
  }, [hash])
}

/** The landing, section for section. The contract for what each section says and does: docs/notes/app/landing.md */
function RouteComponent() {
  useContactHash()

  return (
    // White on the primary green is about 2:1; the near-black the dark theme already uses is about 9:1 (WCAG AA wants 4.5).
    <div className="button-fill-light relative isolate flex flex-col [--primary-foreground:oklch(0.205_0_0)]">
      <LandingNav onContact={requestDemo} />
      <main className="w-full">
        <Hero onDemo={requestDemo} />
        <ProfessionalsSection />
        <Solutions />
        <TrialCta onDemo={requestDemo} />
        <ApiSection onDemo={requestDemo} />
        {FEATURE_FLAGS.showPricing && <PlansSection onDemo={requestDemo} />}
        {/* The close is one dark block, contact and questions running into the footer; `dark` switches the theme tokens.
            Its ground is an ink tinted toward the primary, so the green reads as part of it rather than on top of it. */}
        <div className={cn('dark text-foreground relative isolate overflow-hidden', INK)}>
          <InkBackdrop />
          <FaqSection />
          {/* No muted text in the close: the token takes the full text colour for everything inside, footer included. */}
          <div className="border-t border-white/8 [--muted-foreground:var(--foreground)]">
            {/* The language lives in the top bar from `sm`; on a phone the bar has no room for it, so it stays down here. */}
            <Footer languageClassName="sm:hidden" />
          </div>
        </div>
      </main>
    </div>
  )
}

// A hero CTA: may shrink below its content, its text and padding following the row's width (see the row below).
const HERO_BUTTON = 'min-w-0 shrink px-[clamp(0.4rem,3cqi,0.625rem)] text-[clamp(0.6875rem,4.35cqi,0.875rem)]'

/** The pitch and its proof: copy, CTAs and the two counters on the left, the live profile comparison on the right. */
function Hero(props: { onDemo: () => void }) {
  const { t } = useLingui()

  return (
    // `scroll-mt-17` is the nav's height, so the `#top` anchor lands under the nav. Its height is its content's, not the
    // screen's: the professionals strip starts right under it, so the reader sees there is more below.
    <header
      id="top"
      className="short:pt-14 short:pb-10 relative isolate flex scroll-mt-17 items-center overflow-x-clip pt-24 pb-16 sm:pt-28 sm:pb-20 lg:pt-32"
    >
      <div className="mx-auto grid w-full max-w-6xl grid-cols-[minmax(0,1fr)] items-center gap-10 px-5 sm:px-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:gap-8">
        <div>
          <Appear>
            <h1 className="short:text-[2.5rem] xl:short:text-[2.5rem] text-4xl leading-[1.04] font-semibold tracking-tight text-pretty sm:text-5xl xl:text-[3.45rem]">
              <Trans>
                The software that lets you <em className="text-primary not-italic">take control of talent</em> in your
                company
              </Trans>
            </h1>
          </Appear>
          <Appear delay={80}>
            <p className="text-muted-foreground short:mt-4 mt-5 max-w-[34em] text-lg leading-relaxed text-pretty">
              {t`Take control of your talent strategy. Get to know in depth the Psychographic Profile of your employees, positions and teams, and decide with the confidence that data gives you.`}
            </p>
          </Appear>
          {/* One line always: the row is a container, and the buttons' text and padding scale with its width (`cqi`) down to
              what fits on a narrow phone, instead of the second button wrapping under the first. */}
          <Appear
            delay={160}
            className="short:mt-8 @container mt-10 flex flex-nowrap items-center gap-[min(0.75rem,3cqi)]"
          >
            <Button size="lg" type="button" className={HERO_BUTTON} onClick={props.onDemo}>
              {t`Request a demo`}
              <DemoChevron />
            </Button>
            <ButtonLink
              size="lg"
              variant="outline"
              to="/register/company"
              viewTransition={authViewTransition('enter')}
              className={HERO_BUTTON}
            >
              {t`Try free for 14 days`}
            </ButtonLink>
          </Appear>
          <Appear delay={240} className="short:mt-6 mt-8">
            <LandingCounts>
              <LandingCountsReader />
            </LandingCounts>
          </Appear>
        </div>

        {/* Hidden on phones. Beside the copy from `lg`. Narrower, there is no room for it beside the copy and no point
            in it under the copy: it becomes the hero's backdrop instead, large, low on the right (under the CTAs and
            counters, never the title or description) and mostly off the screen, faded toward the text and downward: the
            hero only clips sideways, so it dissolves below the hero instead of ending in a hard edge. */}
        <Appear
          delay={200}
          className="max-lg:pointer-events-none max-lg:absolute max-lg:top-[47%] max-lg:-right-[88%] max-lg:-z-10 max-lg:w-[34rem] max-sm:hidden sm:max-lg:top-[40%] sm:max-lg:-right-[46%] sm:max-lg:w-[40rem] lg:-mt-4 lg:translate-x-4 lg:self-start"
        >
          <div className="max-lg:[mask-image:linear-gradient(to_right,transparent_8%,black_60%),linear-gradient(to_bottom,black_50%,transparent_92%)] max-lg:[mask-composite:intersect] max-lg:opacity-55">
            <RadarCompare variant={FEATURE_FLAGS.radarVariant} />
          </div>
        </Appear>
      </div>
    </header>
  )
}

// `self-start` keeps the hover target to the words and the chevron, not the card's full width.
const SOLUTION_LINK =
  'group/cta-link solution-accent mt-auto inline-flex items-center gap-1 self-start pt-3.5 text-sm font-semibold'
const SOLUTION_CARD = 'bg-background flex flex-col px-7 pt-8 pb-8'

interface Solution {
  kind: SolutionKind
  title: string
  description: string
  cta: string
}

/** The API card points at this page's own section; every other card starts a registration. */
function SolutionLink(props: { kind: SolutionKind; className: string; children: ReactNode }) {
  return props.kind === 'api' ? (
    <a href="#api" className={props.className}>
      {props.children}
    </a>
  ) : (
    <Link to="/register/company" viewTransition={authViewTransition('enter')} className={props.className}>
      {props.children}
    </Link>
  )
}

/**
 * One piece of the cycle. Two ways into it, picked by `solutionsVariant`: the whole card is the link, which then
 * carries no call to action under the copy and reads as one through `.solution-card-link` (app.css); or the card is
 * static and its copy ends in the call to action.
 */
function SolutionCard(props: { solution: Solution; delay: number }) {
  const wholeCard = FEATURE_FLAGS.solutionsVariant === 'card'
  const className = cn(SOLUTION_CARD, `solution-${props.solution.kind}`, wholeCard && 'solution-card-link')
  const body = (
    <Reveal delay={props.delay} className="flex flex-1 flex-col gap-3 duration-1000">
      <SolutionAnimation kind={props.solution.kind} className="mb-1.5" />
      <h3 className="solution-title text-base font-semibold tracking-tight">{props.solution.title}</h3>
      <p className="text-muted-foreground text-sm leading-relaxed">{props.solution.description}</p>
      {!wholeCard && (
        <SolutionLink kind={props.solution.kind} className={SOLUTION_LINK}>
          {props.solution.cta}
          <LinkChevron />
        </SolutionLink>
      )}
    </Reveal>
  )

  // The title and copy inside name the link, so it needs no label of its own.
  return wholeCard ? (
    <SolutionLink kind={props.solution.kind} className={className}>
      {body}
    </SolutionLink>
  ) : (
    <article className={className}>{body}</article>
  )
}

/** Six pieces covering the whole cycle, one card each, in one hairline grid. */
function Solutions() {
  const { t } = useLingui()
  const solutions: Solution[] = [
    {
      kind: 'evaluate',
      title: t`Evaluate your employees`,
      description: t`Enter your employees' details and invite them to take our test. That way you'll get to know their Psychographic Profile.`,
      cta: t`Invite your employees`,
    },
    {
      kind: 'describe',
      title: t`Describe your positions`,
      description: t`Describe what each of your positions involves and set out the profile of competences, values and personality it requires.`,
      cta: t`Describe your first position`,
    },
    {
      kind: 'teams',
      title: t`Define your teams`,
      description: t`Enter your teams' information and access the Psychographic Profile of the team as a whole and of each of its members.`,
      cta: t`Define your first team`,
    },
    {
      kind: 'market',
      title: t`Explore the market`,
      description: t`Check the standard report for the profiles you're interested in and explore the global market interactively.`,
      cta: t`Access the market`,
    },
    {
      kind: 'cross',
      title: t`Cross all the information`,
      description: t`Compare and connect all the information tied to your profiles and to the market's, and make your talent decisions based on data.`,
      cta: t`Compare profiles`,
    },
    {
      kind: 'api',
      title: t`Integrate it into your platform`,
      description: t`Embed the test and the reports in your platform with our SDK, and read every profile and fit through the API.`,
      cta: t`See the integration`,
    },
  ]
  const gridRef = usePauseOffscreen<HTMLDivElement>()

  return (
    <section id="solutions" className="scroll-mt-20 py-20">
      <div className="mx-auto max-w-6xl px-5 sm:px-6">
        <Reveal className="max-w-2xl duration-1000">
          <h2 className="max-w-[24ch] text-3xl leading-tight font-semibold tracking-tight text-balance sm:text-4xl">
            {t`Six key pieces for a complete talent evaluation`}
          </h2>
          <p className="text-muted-foreground mt-4 text-lg leading-relaxed text-pretty">
            {t`Evaluating talent as a whole means connecting every piece of your organization. If you're a company, you have six essential elements to do it: employees, positions, teams, offers, applicants and the market.`}
          </p>
        </Reveal>

        {/* No outer box: the hairlines are the cards' inner borders, over a border-coloured layer that fades out toward
            every edge (`.solutions-grid` in app.css), so each line dissolves into the page at its ends. */}
        <div ref={gridRef} className="solutions-grid mt-12 grid sm:grid-cols-2 lg:grid-cols-3">
          {/* The cards' contents rise, not the cards: the grid's hairlines are the cards' inner borders, and they must not
              show through while a card fades. Staggered along the reading order. */}
          {solutions.map((solution, index) => (
            <SolutionCard key={solution.title} solution={solution} delay={index * 150} />
          ))}
        </div>
      </div>
    </section>
  )
}

/**
 * The free-trial call to action, right after the Solutions grid: the moment a company reader has seen the whole product.
 * The platforms' panel comes after it, so no integration pitch stands between the product and its call to action.
 */
function TrialCta(props: { onDemo: () => void }) {
  const { t } = useLingui()
  return (
    <section className="px-5 pt-4 pb-24 text-center">
      <Reveal>
        <h2 className="mx-auto max-w-[22em] text-3xl leading-tight font-semibold tracking-tight text-balance sm:text-4xl">
          {t`Start free today and take control of your talent`}
        </h2>
        <p className="text-muted-foreground mx-auto mt-5 max-w-[36em] text-lg leading-relaxed text-pretty">
          {t`Try every feature for 14 days, with no commitment and no card: identify the profiles of your employees, positions and teams, and compare them with the market.`}
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <ButtonLink size="lg" to="/register/company" viewTransition={authViewTransition('enter')}>
            {t`Try free for 14 days`}
          </ButtonLink>
          <Button size="lg" variant="outline" type="button" onClick={props.onDemo}>
            {t`Request a demo`}
            <DemoChevron />
          </Button>
        </div>
      </Reveal>
    </section>
  )
}

function LandingCountsReader() {
  const { data } = useSuspenseQuery(Route.useRouteContext().landingCountsQueryOptions)
  return <LandingCountsValue profilesCount={data.profilesCount} companiesCount={data.companiesCount} />
}
